import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
      
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div>
      {/* Hero Section */}
      <section className="h-[30vh] relative flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: 'url("https://images.pexels.com/photos/1416530/pexels-photo-1416530.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2")',
          }}
        >
          <div className="absolute inset-0 bg-black opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
            Contacteaza-ne
          </h1>
          <p className="text-xl text-white mb-0 max-w-2xl mx-auto">
            Suntem aici pentru a răspunde la orice întrebări ați putea avea
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-sage-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              { 
                icon: <MapPin className="h-8 w-8 text-sage-700" />, 
                title: "Address", 
                content: "Jud. Bihor,Sat.Haieu(Com.Sanmartin),Nr.22",
                link: "https://maps.google.com",
                linkText: "Get Directions"
              },
              { 
                icon: <Phone className="h-8 w-8 text-sage-700" />, 
                title: "Telefon",
                content: "+(04) 0773922165",
                link: "tel:+040773922165",
                linkText: "Suna-ne"
              },
              { 
                icon: <Mail className="h-8 w-8 text-sage-700" />, 
                title: "Email", 
                content: "leahuion68@yahoo.com",
                link: "mailto:leahuion68@yahoo.com",
                linkText: "Trimite Email"
              },
              { 
                icon: <Clock className="h-8 w-8 text-sage-700" />, 
                title: "Ore",
                content: "Check-in: 2:00 PM - 9:00 PM\nCheck-out: 11:00 AM",
                link: "",
                linkText: ""
              },
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-lg shadow-md text-center transition-all hover:shadow-lg"
              >
                <div className="flex justify-center mb-4">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600 whitespace-pre-line mb-4">{item.content}</p>
                {item.link && (
                  <a 
                    href={item.link} 
                    className="text-sage-700 hover:text-sage-800 font-medium"
                    target={item.link.startsWith('http') ? '_blank' : undefined}
                    rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  >
                    {item.linkText}
                  </a>
                )}
              </div>
            ))}
          </div>
          
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Map */}
            <div className="w-full lg:w-1/2">
              <div className="bg-white rounded-lg shadow-md overflow-hidden h-full">
                <div className="h-96 w-full bg-gray-200 flex items-center justify-center">
                  <p className="text-gray-500">Map integration would appear here</p>
                  {/* Note: Implement map integration here when possible */}
                </div>
              </div>
            </div>
            
            {/* Contact Form */}
            <div className="w-full lg:w-1/2">
              <div className="bg-white rounded-lg shadow-md p-8">
                <h2 className="text-2xl font-serif font-bold text-gray-800 mb-6">Trimite-ne un mesaj</h2>
                
                {success ? (
                  <div className="bg-green-50 border border-green-200 rounded-md p-6 text-center">
                    <div className="flex justify-center mb-4">
                      <div className="bg-green-100 rounded-full p-2">
                        <Send className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Mesaj trimis!</h3>
                    <p className="text-gray-600 mb-4">
                      Vă mulțumim că ne-ați contactat. Vă vom reveni cât mai curând posibil.
                    </p>
                    <button
                      onClick={() => setSuccess(false)}
                      className="text-sage-700 hover:text-sage-800 font-medium"
                    >
                      Trimite alt mesaj.
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2" htmlFor="name">
                          Nume
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 font-medium mb-2" htmlFor="email">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <label className="block text-gray-700 font-medium mb-2" htmlFor="subject">
                        Subiect
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500 appearance-none"
                      >
                        <option value="">Selectați un subiect</option>
                        <option value="booking">Cerere de rezervare</option>
                        <option value="info">Informații generale</option>
                        <option value="feedback">Feedback</option>
                        <option value="other">Altul</option>
                      </select>
                    </div>
                    
                    <div className="mb-6">
                      <label className="block text-gray-700 font-medium mb-2" htmlFor="message">
                        Mesaj
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                      ></textarea>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-sage-700 hover:bg-sage-800 text-white py-3 rounded-md transition-colors text-center block font-medium disabled:bg-gray-400"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-gray-800 mb-3">
              Întrebări frecvente
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Găsiți răspunsuri la cele mai frecvente întrebări despre casa noastră de oaspeți.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {[
              { 
                question: "Care sunt orele dumneavoastră de check-in și check-out?",
                answer: "\n" +
                    "Check-in se face de la 14:00 la 21:00. Check-out se face până la ora 11:00. Dacă aveți nevoie de check-in devreme sau check-out târziu, vă rugăm să ne contactați în avans pentru a aranja dacă este posibil."
              },
              { 
                question: "Oferiti micul dejun sau alt mese?",
                answer: "Nu, nu oferim aceasta facilitate."
              },
              { 
                question: "Există parcare disponibilă?\n",
                answer: "Da, oferim parcare gratuită pentru toți oaspeții noștri. Zona de parcare este bine iluminată și securizată."
              },
              { 
                question: "Aveti o politică de anulare?",
                answer: "Politica noastră standard permite anularea gratuită cu până la 72 de ore înainte de check-in. Anulările în termen de 72 de ore de la sosire pot fi supuse unei taxe de o noapte. Vă rugăm să consultați rezervarea dvs. pentru detalii."
              },

            ].map((faq, index) => (
              <div 
                key={index}
                className="mb-4 bg-sage-50 rounded-lg overflow-hidden"
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer p-6">
                    <h3 className="text-lg font-bold text-gray-800">{faq.question}</h3>
                    <span className="ml-6 flex-shrink-0 text-sage-700 group-open:rotate-180 transition-transform duration-300">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 pb-6 text-gray-600">
                    <p>{faq.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;